import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

export default function AiMarkdown({ content }: { content: string }) {
  if (!content.trim()) {
    return (
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
        Nothing yet.
      </p>
    );
  }

  return (
    <div
      style={{
        fontSize: 'var(--text-sm)',
        lineHeight: 'var(--leading-normal)',
        color: 'var(--text-body)',
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          p: ({ children }) => <p style={{ margin: '0 0 8px' }}>{children}</p>,
          h1: ({ children }) => (
            <h1
              style={{
                margin: '0 0 8px',
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              }}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              style={{
                margin: '12px 0 8px',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              }}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              style={{
                margin: '12px 0 6px',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              }}
            >
              {children}
            </h3>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="hover-underline"
              style={{ color: 'var(--accent-text)', cursor: 'pointer' }}
            >
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <a href={src} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
              <img
                src={src}
                alt={alt || ''}
                loading="lazy"
                style={{
                  maxWidth: '100%',
                  maxHeight: '320px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-hairline)',
                  display: 'block',
                  margin: '4px 0',
                }}
              />
            </a>
          ),
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-xs)',
                    background: 'var(--surface-raised)',
                    border: '1px solid var(--border-hairline)',
                    borderRadius: 'var(--radius-xs)',
                    padding: '1px 5px',
                    color: 'var(--text-body)',
                  }}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                style={{
                  display: 'block',
                  padding: '12px',
                  margin: '8px 0',
                  overflowX: 'auto',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-body)',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {children}
              </code>
            );
          },
          ul: ({ children }) => (
            <ul style={{ margin: '0 0 8px', paddingLeft: '18px' }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '0 0 8px', paddingLeft: '18px' }}>{children}</ol>
          ),
          li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
          blockquote: ({ children }) => (
            <blockquote
              style={{
                margin: '0 0 8px',
                padding: '6px 12px',
                borderLeft: '2px solid var(--accent)',
                color: 'var(--text-muted)',
              }}
            >
              {children}
            </blockquote>
          ),
          strong: ({ children }) => (
            <strong style={{ fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>
              {children}
            </strong>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
